<template>
  <div class="container-xxl">
    <div class="card card-flush sncr-callback">
      <div class="card-body text-center">
        <template v-if="state === 'working'">
          <div class="spinner-border text-primary mb-4" role="status"></div>
          <h3 class="sncr-callback-title">Concluindo a autenticação no gov.br</h3>
          <p class="text-muted">Estamos guardando sua sessão e buscando novas numerações. Não feche esta página.</p>
        </template>

        <template v-else-if="state === 'done'">
          <i class="bi sncr-callback-icon" :class="doneIcon"></i>
          <h3 class="sncr-callback-title">{{ doneTitle }}</h3>
          <p class="text-muted">{{ doneMessage }}</p>
          <button type="button" class="btn btn-primary" @click="goBack">Voltar para onde eu estava</button>
        </template>

        <template v-else>
          <i class="bi bi-exclamation-triangle-fill text-danger sncr-callback-icon"></i>
          <h3 class="sncr-callback-title">Não foi possível concluir</h3>
          <p class="text-muted">{{ errorMessage }}</p>
          <div class="d-flex justify-content-center gap-2">
            <button type="button" class="btn btn-primary" :disabled="retrying" @click="retry">
              Autenticar novamente
            </button>
            <button type="button" class="btn btn-light" @click="goBack">Voltar</button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import {clearState, consumeReturn, exchangeSession, matchesState, sncrError, startGovBrAuth} from "../../helpers/sncr";

export default {
  name: "SncrCallback",
  data() {
    return {
      state: 'working',
      errorMessage: '',
      doneMessage: '',
      retrying: false,
      returnTo: null,
    }
  },
  created() {
    this.returnTo = consumeReturn();
    this.exchange();
  },
  computed: {
    userAuth() {
      return this.$store.state.auth.user;
    },
    doneTypes() {
      return (this.$store.state.sncr?.balance?.inscriptions ?? [])
          .flatMap((inscription) => inscription.types ?? []);
    },
    hasLowBalance() {
      return this.doneTypes.some((type) => type.status !== 'COMFORTABLE');
    },
    doneTitle() {
      return this.hasLowBalance ? 'Autenticação concluída' : 'Tudo certo';
    },
    doneIcon() {
      return this.hasLowBalance
          ? 'bi-exclamation-circle-fill text-warning'
          : 'bi-check-circle-fill text-success';
    },
  },
  methods: {
    exchange() {
      const sessionId = this.$route.query.session_id ?? this.$route.query.sessionId;
      const professionalId = this.returnTo?.professionalId ?? this.userAuth?.id_prof;

      if (!matchesState(this.$route.query.state)) {
        return this.fail('O retorno do gov.br não corresponde à autenticação que começamos aqui. Tente novamente.');
      }

      clearState();

      if (!sessionId) {
        return this.fail('O gov.br não devolveu a sessão. Tente autenticar novamente.');
      }

      if (!professionalId) {
        return this.fail('Não identificamos o profissional desta autenticação. Entre novamente e tente pelo seu perfil.');
      }

      exchangeSession(professionalId, sessionId).then((data) => {
        this.$store.dispatch('sncr/apply', {balance: data, professionalId});
        this.state = 'done';
        this.doneMessage = this.describe(data);
      }).catch((e) => {
        this.fail(sncrError(e).message);
      });
    },
    describe(balance) {
      const types = (balance?.inscriptions ?? []).flatMap((inscription) => inscription.types ?? []);
      const available = types.filter((type) => (type.balance ?? 0) > 0);
      const missing = types.filter((type) => (type.balance ?? 0) <= 0);
      const low = types.filter((type) => type.status !== 'COMFORTABLE');
      const visaOnly = low.length > 0 && low.every((type) => type.requires_visa_authorization);

      if (available.length === 0) {
        return 'Sua sessão do gov.br foi salva. Ainda não há numerações em estoque. A tentativa de obter novos números foi iniciada e pode levar alguns instantes.';
      }

      const currentBalance = available
          .map((type) => `${type.balance} de ${type.regulatory_type_description}`)
          .join(', ');

      if (missing.length > 0) {
        const missingTypes = missing.map((type) => type.regulatory_type_description).join(', ');

        return `Sua sessão do gov.br foi salva. Saldo atual: ${currentBalance}. Ainda sem numeração: ${missingTypes}. A tentativa de reabastecimento foi iniciada.`;
      }

      if (low.length > 0) {
        const currentBalance = low
            .map((type) => `${type.balance} de ${type.regulatory_type_description}`)
            .join(', ');
        const authorizationNotice = visaOnly
            ? ' Novos números dependem da autorização prévia da vigilância sanitária do seu estado.'
            : '';

        return `Sua sessão do gov.br foi salva. O saldo continua baixo: ${currentBalance}.${authorizationNotice}`;
      }

      return `Sua sessão do gov.br foi salva. Saldo atual: ${currentBalance}.`;
    },
    fail(message) {
      this.state = 'error';
      this.errorMessage = message;
    },
    retry() {
      const professionalId = this.returnTo?.professionalId ?? this.userAuth?.id_prof;

      if (!professionalId) {
        return;
      }

      this.retrying = true;
      startGovBrAuth(professionalId, this.returnTo).catch(() => {
        this.retrying = false;
        this.fail('Não foi possível abrir a autenticação do gov.br. Tente novamente.');
      });
    },
    goBack() {
      const path = this.returnTo?.path;

      if (path && path !== this.$route.fullPath) {
        return this.$router.replace(path);
      }

      this.$router.replace('/attendances');
    },
  },
}
</script>

<style scoped>
.sncr-callback {
  max-width: 560px;
  margin: 48px auto;
}

.sncr-callback-icon {
  font-size: 2.5rem;
  display: block;
  margin-bottom: 12px;
}

.sncr-callback-title {
  color: #212529;
  margin-bottom: 8px;
}
</style>
